import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  CheckCircle, ArrowForward, ExpandMore,
  School, Groups, AdminPanelSettings, Email,
  Shield, Speed, Storage, DomainVerification,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: 'Bulk', label: 'User Provisioning' },
  { value: 'Multi', label: 'Domain Support' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '₹0', label: 'Setup Fee' },
];

const pillars = [
  {
    color: '#0891b2',
    icon: <Groups sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Bulk User Management',
    desc: 'Provision hundreds of student or staff accounts at the start of a new semester — via CSV upload or the admin panel. Deactivate accounts in bulk when the year ends.',
    points: [
      'Bulk add/remove users via CSV',
      'Set per-user storage quotas easily',
      'Separate student and staff domains',
      'Suspend accounts without deleting mail',
    ],
  },
  {
    color: '#dc2626',
    icon: <AdminPanelSettings sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Multi-Domain Administration',
    desc: 'Run students on students.college.edu and faculty on college.edu — from the same admin console. Each domain gets its own DKIM keys, branding, and catch-all routing.',
    points: [
      'Manage multiple domains in one place',
      'Per-domain branding and customisation',
      'Independent DKIM keys per domain',
      'Catch-all routing to any mailbox',
    ],
  },
  {
    color: '#7c3aed',
    icon: <Shield sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Safe, Accountable Communication',
    desc: 'All email runs through Rspamd spam and phishing detection. Admin-managed 2FA, full audit logs, and role-based access ensure accountability across faculty and admin staff.',
    points: [
      'Rspamd spam and phishing filter',
      'Admin-enforced 2FA for staff accounts',
      'Full admin audit trail',
      'Data stored in India (DPDP aligned)',
    ],
  },
];

const features = [
  { icon: <School sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'Student Email Accounts', desc: 'Dedicated @student.college.edu addresses, managed from a single admin panel.' },
  { icon: <DomainVerification sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'Multiple Domains', desc: 'Faculty, students, departments — each on their own sub-domain if needed.' },
  { icon: <Email sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Aliases & Distribution Lists', desc: 'department@, batch2025@, staff@ — route to the right people automatically.' },
  { icon: <Storage sx={{ fontSize: 22, color: '#059669' }} />, title: 'Configurable Storage Quotas', desc: 'Set different quotas for students and faculty. Expand any mailbox in seconds.' },
  { icon: <Speed sx={{ fontSize: 22, color: '#d97706' }} />, title: 'Fast & Reliable', desc: '99.9% uptime SLA — even during admissions, results, and exam seasons.' },
  { icon: <Shield sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'Anti-Spam & Phishing', desc: 'Protect students from phishing emails that impersonate the institution.' },
];

const faqs = [
  { q: 'Can we provision 500 student accounts at once?', a: 'Yes. Bulk user provisioning via CSV is supported from the admin panel. Upload a spreadsheet with usernames, display names, and passwords — accounts are created instantly. You can also use the API to integrate with your existing student information system.' },
  { q: 'Can students have a different domain from faculty?', a: 'Yes. CruvzMail supports multiple domains from a single admin console. Students can be on student.yourinstitution.edu, faculty on yourinstitution.edu, and administration on admin.yourinstitution.edu — each independently configured with its own DKIM, MX, and branding.' },
  { q: 'How do we deactivate a student account when they graduate?', a: 'From the admin panel, you can suspend or delete accounts individually or in bulk. Suspending an account disables login but preserves the mailbox. You can also set a future date for automatic account expiry — useful for managing batch graduations.' },
  { q: 'Is the email data stored in India?', a: 'Yes. All data is stored in our own Indian datacenter infrastructure, aligning with the DPDP Act 2023. We do not use AWS, Azure, or international cloud providers for student or staff email storage.' },
  { q: 'Do you offer special pricing for educational institutions?', a: 'Yes. We offer discounted per-mailbox pricing for registered educational institutions. Contact us with your institution\'s details and expected mailbox count for a custom quote.' },
];

const EducationPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #082f49 50%, #0f172a 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(8,145,178,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Education" sx={{ mb: 3, bgcolor: 'rgba(8,145,178,0.15)', color: '#67e8f9', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(8,145,178,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #67e8f9 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Trusted Email for<br />Educational Institutions
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 640, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Manage student and faculty email accounts from one admin console — bulk provisioning, multi-domain support, and spam protection for thousands of users, without the complexity.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#0891b2', boxShadow: '0 0 40px rgba(8,145,178,0.4)', '&:hover': { bgcolor: '#0e7490' } }}>
              Get a Quote for Your Institution
            </Button>
            <Button onClick={() => navigate('/features')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View All Features
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#67e8f9', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.5 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Built for Institutions</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Manage Thousands, from One Dashboard
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              From bulk provisioning to multi-domain routing — everything an institution needs, without enterprise IT complexity.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff', position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FEATURES ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1 }}>
              Designed for Academic Scale
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>Everything included — from student inboxes to admin controls.</Typography>
          </Box>
          <Grid container spacing={2.5}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fafafa', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } }}>
                  <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.6 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>Education FAQ</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' }, '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#a5f3fc' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#f0f9ff' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#f0f9ff' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, bgcolor: '#0f172a', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(8,145,178,0.1) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 620, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Email for Every Student<br />and Every Faculty Member
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.85 }}>
            Institutional pricing available. Contact us with your student and staff numbers for a custom quote.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }}>
              Get Institutional Pricing
            </Button>
            <Button onClick={() => navigate('/features')} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Explore Features
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default EducationPage;
